import { useEffect, useState, memo } from "react";
import { useReverseGeocode } from "@/hooks/useReverseGeocode";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "@/hooks/useDebounce";
import { 
  Eye, 
  FileText,
  Download,
  RefreshCw,
  Search,
  ArrowUpDown,
  Paperclip,
  Users,
  Play
} from "lucide-react";
import { HeaderPage } from "@/components/HeaderPage";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Loading } from "@/components/Loading";
import { TriageFilters } from "@/components/TriageFilters";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import type { TriageQueueFilters, OccurrenceQueueItem } from "@/dtos/TriageQueueDto";

const TriageLocationCell = memo(({ occurrence }: { occurrence: OccurrenceQueueItem }) => {
  const coords: [number, number] | null =
    occurrence.latitude && occurrence.longitude
      ? [occurrence.latitude, occurrence.longitude]
      : null;

  // Detectar se o endereço parece ser coordenadas brutas (ex: "-3.7327, -38.527")
  const looksLikeCoords = occurrence.address
    ? /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(occurrence.address.trim())
    : true;

  const fallback = !looksLikeCoords ? occurrence.address : undefined;

  const { address, isLoading } = useReverseGeocode(coords, fallback);

  if (isLoading) {
    return <p className="text-sm text-gray-400">Buscando endereço...</p>;
  }

  const displayAddress = address || occurrence.address;
  const neighborhood = occurrence.neighborhood !== "N/A" ? occurrence.neighborhood : "";

  return (
    <p className="text-sm text-gray-600">
      {displayAddress}{neighborhood ? `, ${neighborhood}` : ""}
    </p>
  );
});
TriageLocationCell.displayName = "TriageLocationCell";
import { 
  useTriageQueue,
  useLogOccurrenceView,
  useExportQueue,
  useLogOccurrenceAccess
} from "@/hooks/useOccurrences";
import {
  getTimeIndicator,
  getTimeIndicatorColor,
  formatTimeInQueue,
} from "@/dtos/TriageQueueDto";

export const Triage = () => {
  const navigate = useNavigate();
  const logView = useLogOccurrenceView();
  const exportQueue = useExportQueue();
  const logAccess = useLogOccurrenceAccess();

  // Estados
  const [filters, setFilters] = useState<TriageQueueFilters>({
    limit: 50,
    sortBy: "createdAt",
    sortOrder: "asc",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState(30);
  
  // Debounce do searchTerm para evitar muitas requisições
  const debouncedSearch = useDebounce(searchTerm, 500);
  
  // Filtros com search debounced
  const activeFilters = {
    ...filters,
    search: debouncedSearch,
  };

  // Dados da fila com scroll infinito
  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch
  } = useTriageQueue(activeFilters);

  // Loading full-page apenas no primeiro carregamento (sem dados ainda)
  const isInitialLoading = isLoading && !data;

  // Combinar todas as páginas em uma lista única
  const occurrences = data?.pages.flatMap(page => page.data) || [];
  const totalOccurrences = data?.pages[0]?.total || 0;

  // Scroll infinito: carregar mais dados quando chegar perto do fim
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.innerHeight + window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Carregar mais quando estiver a 200px do final
      if (scrollPosition >= documentHeight - 200 && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  // Contador regressivo para próxima atualização automática
  useEffect(() => {
    setSecondsUntilRefresh(30); // Reset quando os dados mudarem
    
    const interval = setInterval(() => {
      setSecondsUntilRefresh((prev) => {
        if (prev <= 1) {
          return 30; // Reset para 30 segundos
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [data]);
  // Verificar permissão de acesso (RN-001)
  useEffect(() => {
    const userStr = localStorage.getItem("duas-vidas-user");
    if (userStr) {
      const user = JSON.parse(userStr);
      const allowedRoles = ["TRIAGE", "AUDIT", "ADMIN"];
      
      if (!allowedRoles.includes(user.role)) {
        toast.error("Você não tem permissão para acessar esta página");
        navigate("/home");
      }
    }
  }, [navigate]);

  // Controle de acesso: ADMIN vê tudo, AUDIT só visualiza, TRIAGE pode analisar
  const getUserRole = (): "ADMIN" | "AUDIT" | "TRIAGE" | "OTHER" => {
    const userStr = localStorage.getItem("duas-vidas-user");
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.role as "ADMIN" | "AUDIT" | "TRIAGE" | "OTHER";
    }
    return "OTHER";
  };

  const userRole = getUserRole();
  const canAnalyze = userRole === "ADMIN" || userRole === "TRIAGE";
  const canOnlyView = userRole === "AUDIT";

  console.log("[Triage] userRole:", userRole, "canAnalyze:", canAnalyze, "canOnlyView:", canOnlyView);

  // Handlers
  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleSort = (column: "createdAt" | "waitingTime") => {
    const newOrder = 
      filters.sortBy === column && filters.sortOrder === "asc" ? "desc" : "asc";
    
    setFilters({
      ...filters,
      sortBy: column,
      sortOrder: newOrder,
    });
  };

  const handleViewOccurrence = (occurrence: OccurrenceQueueItem) => {
    // Registrar log de visualização (RN-007)
    logView.mutate(occurrence.id);
    
    // Navegar para detalhes
    navigate(`/occurrences/${occurrence.protocol}`);
  };

  const handleExport = async (format: "xlsx" | "csv") => {
    try {
      const blob = await exportQueue.mutateAsync({ format, filters });
      
      // Criar download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fila-triagem-${new Date().toISOString().split("T")[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Arquivo exportado com sucesso!`);
    } catch {
      toast.error("Erro ao exportar arquivo");
    }
  };

  const toggleRowExpansion = (id: string) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  if (isInitialLoading) {
    return <Loading label="Carregando fila de triagem..." />;
  }

  // Dados já estão em 'occurrences' e 'totalOccurrences' definidos acima

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <HeaderPage
        title="Fila de Triagem"
        description={
          canOnlyView
            ? "Visualize as ocorrências aguardando análise (modo visualização)"
            : "Gerencie e analise ocorrências aguardando triagem"
        }
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {/* Indicador de Role */}
        {canOnlyView && (
          <Card className="p-3 bg-blue-50 border-blue-200">
            <div className="flex items-center gap-2 text-blue-700">
              <Eye className="w-4 h-4" />
              <span className="text-sm font-medium">
                Modo Auditoria - Apenas visualização
              </span>
            </div>
          </Card>
        )}

        {/* Contador e Ações */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary/10 rounded-lg">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {totalOccurrences}
                </h3>
                <p className="text-sm text-gray-600">
                  {totalOccurrences === 1 ? "Ocorrência" : "Ocorrências"} na fila
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="text-sm text-gray-500 mr-2">
                Atualização automática em <span className="font-semibold text-primary">{secondsUntilRefresh}s</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="cursor-pointer"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Atualizar
              </Button>

              <Select onValueChange={(value) => handleExport(value as "xlsx" | "csv")}>
                <SelectTrigger className="w-[140px]">
                  <Download className="w-4 h-4 mr-2" />
                  Exportar
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Busca */}
        <Card className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Buscar por protocolo, nome do cidadão, categoria, subcategoria, endereço..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </Card>

        {/* Filtros */}
        <TriageFilters filters={filters} onFiltersChange={setFilters} />

        {/* Tabela de Ocorrências */}
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]"></TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("createdAt")}
                      className="cursor-pointer font-semibold"
                    >
                      Protocolo / Data
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead className="font-semibold">Categoria</TableHead>
                  <TableHead className="font-semibold">Bairro</TableHead>
                  <TableHead>Cidadão</TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("waitingTime")}
                      className="cursor-pointer font-semibold"
                    >
                      Tempo na Fila
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead className="w-[120px]">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isFetching && !isFetchingNextPage ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      <div className="flex items-center justify-center gap-2">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Atualizando...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : occurrences.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      Nenhuma ocorrência encontrada na fila
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {occurrences.map((occurrence) => {
                      const indicator = getTimeIndicator(occurrence.timeInQueue);
                      const colorClass = getTimeIndicatorColor(indicator);
                      const isExpanded = expandedRow === occurrence.id;
                        console.log(occurrence)
                      return (
                        <motion.tr
                          key={occurrence.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className={`border-b hover:bg-gray-50 transition-colors ${
                            occurrence.isBeingAnalyzed ? "bg-yellow-50" : ""
                          }`}
                        >
                          <TableCell colSpan={7} className="p-0">
                            <div>
                              {/* Linha principal */}
                              <div className="grid grid-cols-[50px_1fr_1fr_1fr_1fr_1fr_120px] gap-4 p-4">
                                {/* Ícone de expansão */}
                                <div className="flex items-center">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleRowExpansion(occurrence.id)}
                                    className="cursor-pointer p-1"
                                  >
                                    {isExpanded ? "−" : "+"}
                                  </Button>
                                </div>

                                {/* Protocolo e Data */}
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-900">
                                      {occurrence.protocol}
                                    </span>
                                    {occurrence.isReopened && (
                                      <Badge variant="outline" className="text-xs">
                                        Reaberta
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500">
                                    {new Date(occurrence.createdAt).toLocaleString("pt-BR")}
                                  </p>
                                </div>

                                {/* Categoria */}
                                <div className="space-y-1">
                                  <p className="font-medium text-gray-900">
                                    {occurrence.category.name}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    {occurrence.subcategory.name}
                                  </p>
                                </div>

                                {/* Bairro */}
                                <div>
                                  <p className="text-sm text-gray-900">
                                    {occurrence.neighborhood}
                                  </p>
                                </div>

                                {/* Cidadão */}
                                <div className="space-y-1">
                                  <div className="flex items-center gap-1.5">
                                    <p className="text-sm font-medium text-gray-900">
                                      {occurrence.isAnonymous
                                        ? "Usuário Anônimo"
                                        : occurrence.citizen.fullName}
                                    </p>
                                    {occurrence.isAnonymous && (
                                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-purple-300 text-purple-700 bg-purple-50">
                                        Anônima
                                      </Badge>
                                    )}
                                  </div>
                                  {occurrence.hasAttachments && (
                                    <div className="flex items-center gap-1 text-xs text-gray-500">
                                      <Paperclip className="w-3 h-3" />
                                      {occurrence.attachmentCount} anexo(s)
                                    </div>
                                  )}
                                </div>

                                {/* Tempo na Fila */}
                                <div>
                                  <Badge
                                    className={`${colorClass} border`}
                                    variant="outline"
                                  >
                                    {formatTimeInQueue(occurrence.timeInQueue)}
                                  </Badge>
                                  {occurrence.isBeingAnalyzed && occurrence.analyzer && (
                                    <div className="flex items-center gap-1 mt-2 text-xs text-yellow-700">
                                      <Users className="w-3 h-3" />
                                      <span className="font-medium">
                                        {occurrence.analyzer?.profile?.fullName || occurrence.analyzer?.email}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {/* Ações */}
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      
                                      // Registrar auditoria
                                      logAccess.mutate({
                                        occurrenceId: occurrence.id,
                                        protocol: occurrence.protocol,
                                        action: "TRIAGE_STARTED",
                                      });
                                      
                                      // Navegar para tela de triagem
                                      navigate(`/occurrences/${occurrence.protocol}/screening`);
                                    }}
                                    className="cursor-pointer text-white"
                                  >
                                    <Play className="w-4 h-4 mr-1" />
                                    {canAnalyze ? "Triagem" : "Visualizar"}
                                  </Button>
                                </div>
                              </div>

                              {/* Preview expandido (CA-007) */}
                              <AnimatePresence>
                                {isExpanded && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="border-t bg-gray-50"
                                  >
                                    <div className="p-4 space-y-3">
                                      <div>
                                        <h4 className="text-sm font-semibold text-gray-700 mb-1">
                                          Descrição
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                          {occurrence.description}
                                        </p>
                                      </div>

                                      <div>
                                        <h4 className="text-sm font-semibold text-gray-700 mb-1">
                                          Localização
                                        </h4>
                                        <TriageLocationCell occurrence={occurrence} />
                                      </div>

                                      {occurrence.hasAttachments && (
                                        <div>
                                          <h4 className="text-sm font-semibold text-gray-700 mb-1">
                                            Anexos
                                          </h4>
                                          <p className="text-sm text-gray-600">
                                            {occurrence.attachmentCount} arquivo(s) anexado(s)
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          </TableCell>
                        </motion.tr>
                      );
                    })}
                  </>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Indicador de carregamento de mais itens */}
          {isFetchingNextPage && (
            <div className="flex items-center justify-center py-8 border-t">
              <Loading label="Carregando mais ocorrências..." />
            </div>
          )}
          
          {!hasNextPage && occurrences.length > 0 && (
            <div className="text-center py-6 text-sm text-gray-500 border-t">
              Todas as {totalOccurrences} ocorrências foram carregadas
            </div>
          )}
        </Card>

        {/* Legenda de Indicadores */}
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Indicadores de Tempo na Fila
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm text-gray-600">Até 4 horas</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span className="text-sm text-gray-600">4 a 8 horas</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span className="text-sm text-gray-600">8 a 24 horas</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-sm text-gray-600">Acima de 24h (Crítico)</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
